import {json, texture, MiniSignal, spine, delay} from "p3";
import common from "../common";
import definitions from "../definitions";
import CoinAnimation from "../game/actor/misc/CoinAnimation";
import QuestFactory from "../game/quest/QuestFactory";
import settings from "../settings";
import CoinCounterPanel from "../ui/CoinCounterPanel";
import HealthBar from "../ui/health/HealthBar";
import IdleMessage from "../ui/idlemessage/IdleMessage";
import ScrollBar from "../ui/ScrollBar";
import Swiper from "../ui/Swiper";
import TextFormat from "../ui/TextFormat";
import {iswebfont, locale, resizeLabelToFitText} from "../utils";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";
import Scene from "./Scene";

export default class ShopScene extends MenuScene {
    constructor() {
        super("shop");

        this._menuHolder = null;
        this._menuContent = null;
        this._menuMask = null;
        this._scrollbar = null;
        this._swiper = null;
        this._popUpFill = null;

        this._shopItems = [];
        this._purchasingItems = 0;

        this._inventoryButton = null;
        this._closeButton = null;

        this._winky = null;

        this._healthBar = null;

        this._boughtCompass = false;

        this.signals.locationJump = new MiniSignal();
    }

    init() {
        super.init(); 
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        //console.log(this._questLog);

        this.interactive = true;

        if (!common.saveData.settings.musicMute) {
            P3.audio.playMusic("music_shop_loop");
        }
        P3.audio.playSound("vo_bert_hahum_00");

        this.createFill();
 
        this._menuHolder = new PIXI.Container();
        this._menuHolder.x = common.STAGE_WIDTH/2;
        this._menuHolder.y = 150;
        this.addChild(this._menuHolder);

        const backing = new PIXI.NineSlicePlane(texture('shop_bg_9slice'), 60, 40, 60, 40);
        backing.width = 750;
        backing.height = 500;
        backing.position.set(-(backing.width/2), 40);
        this._menuHolder.addChild(backing);

        let menuContentHolder = new PIXI.Container();
        menuContentHolder.y = 30;
        this._menuHolder.addChild(menuContentHolder);

        this._menuContent = new PIXI.Container();
        this._menuContent.scrollPos = 0;
        menuContentHolder.addChild(this._menuContent);

        this._menuMask = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._menuMask.tint = 0x000000;
        this._menuMask.anchor.x = 0.5;
        this._menuMask.y = menuContentHolder.y;
        this._menuMask.width = 750;
        this._menuMask.height = 495;
        menuContentHolder.mask = this._menuMask;
        this._menuHolder.addChild(this._menuMask);

        const shopWindow = new PIXI.Sprite(texture('shop_fg'));
        shopWindow.y = -75;
        shopWindow.anchor.set(0.44, 0);
        this._menuHolder.addChild(shopWindow);

        this.createTitleBanner(locale('ui_shop_title'));

        this._coinHolder = new CoinCounterPanel();
        this._coinHolder.x = -5;
        this._coinHolder.y = 50;
        this._titleBanner.addChild(this._coinHolder);
        this._coinHolder.coinAmount = common.saveData.activeSlot.coins;

        this._inventoryButton = P3.Button.create(texture("btn_inventory"));
        this._inventoryButton.overSoundName = "sfx_btn_generic_rollover";
        this._inventoryButton.clickSoundName = "sfx_btn_bag_open";
        this._inventoryButton.interactive = true;
        this._inventoryButton.defaultSize = {width:this._inventoryButton.width, height:this._inventoryButton.height};
        this.signalListeners.push(
            this._inventoryButton.signals.click.add(() => {
                this.animateHide(() => {
                    this.signals.next.dispatch('inventory');
                });
            })
        );
        this.addChild(this._inventoryButton);

        this._closeButton = this.addCloseButton(this._menuHolder.x + 400, this._menuHolder.y);
        this._closeButton.overSoundName = "sfx_btn_generic_rollover";
        this._closeButton.clickSoundName = "sfx_btn_quests_close";

        this._fillShelves();

        this._winky = new PIXI.spine.Spine(spine("char_winky"));
        this._winky.skeleton.setSlotsToSetupPose();
        this._winky.skeleton.setToSetupPose();
        this._winky.autoUpdate = false;
        this._winky.state.setAnimation(0, 'idle_00', true);
        this._winky.position.set((common.STAGE_WIDTH/2)-430, common.STAGE_HEIGHT*2);
        this._winky.scale.set(0.5, 0.5);
        this.addChild(this._winky);

        this.resize();

        this.animateShow();
    }

    destroy() {
        this._closeButton.destroy();

        if(this._scrollbar) {
            this._scrollbar.destroy();
        }
        if(this._swiper) {
            this._swiper.destroy();
        }

        P3.audio.stopMusic();

        super.destroy();
    }

    update() {
        super.update();

        if(this._scrollbar){
            this._scrollbar.update();
            const h = -(this._menuContent.height) + this._menuMask.height;

            if(this._scrollbar.dragging) {
                this._menuContent.scrollPos = this._scrollbar.value;
                this._menuContent.y = h * this._menuContent.scrollPos;
            }
            else if(this._swiper){
                this._swiper.update();
                this._menuContent.y = (h * this._menuContent.scrollPos) + this._swiper.y;
                this._scrollbar.value = Math.min(1, Math.max(0, this._menuContent.y / h));
                if (!this._swiper.dragging) {
                    this._menuContent.scrollPos = this._scrollbar.value;
                }
                if (this._menuContent.y > 0) {
                    this._menuContent.y = 0;
                    this._menuContent.scrollPos = 0;
                }
                else if (this._menuContent.y < h) {
                    this._menuContent.y = h;
                    this._menuContent.scrollPos = 1;
                }
                else {
                    this._scrollbar.playSound(Math.abs(this._swiper.y) > 5 ? -this._swiper.y : 0);
                }
                if (!this._swiper.dragging) {
                    this._swiper.reset();
                }
            }
        }
        
        if(this.isSceneLoaded) {
            if (this._winky) {
                this._winky.update(window.dt);
            }
            if (this._healthBar) {
                this._healthBar.update();
            }
        }
    }

    resize()
    {
        super.resize();

        if (this.isSceneLoaded) {
            this._inventoryButton.position.set(
                ((common.STAGE_WIDTH - P3.View.width + this._inventoryButton.defaultSize.width) * 0.5) + 15,
                (this._inventoryButton.defaultSize.height * 0.5) + 30
            );
            this._resizePopUpFill();
        }
    }

    _fillShelves(){
        const shopData = json('shop').slice();

        for (let i = shopData.length-1; i >= 0; i--) {
            if (shopData[i].price == null && shopData[i].ad && !common.ads.isAdEnabled(shopData[i].ad)) {
                shopData.splice(i, 1);
            }
        }

        const slot = common.saveData.activeSlot;
        const itemsPerShelf = 3;
        let data, button, priceTag, priceHolder, coinIcon, coinLabel, isAd, adBacking, viewAdLabelHolder, viewAdLabel;
        const xPositions = [123, 327, 538];
        for (let i = 0; i < Math.ceil(shopData.length / itemsPerShelf); i++) {
            let shelf = new PIXI.Sprite(texture('shop_shelf'));
            shelf.x = -(shelf.width/2);
            shelf.y = i * shelf.height;
            this._menuContent.addChild(shelf);

            for (let j = 0; j < itemsPerShelf; j++) {
                data = shopData[(i*itemsPerShelf)+j];
                if (data) {

                    isAd = false;
                    if(data.ad && common.ads.isAdEnabled(data.ad)) {
                        isAd = true;

                        adBacking = new PIXI.Sprite(texture('shop_shelf_ad_overlay'));
                        adBacking.anchor.set(0.5, 0);
                        adBacking.position.set(xPositions[j] - 5, 59);
                        shelf.addChild(adBacking);

                        viewAdLabelHolder = new PIXI.NineSlicePlane(texture('ad_label'), 17, 7, 17, 7);
                        shelf.addChild(viewAdLabelHolder);

                        if (!iswebfont()) {
                            viewAdLabel = new PIXI.BitmapText(locale('ui_watch_ad'), {
                                fontName: "Alghera Pro",
                                fontSize: 18,
                                align: "left",
                                tint: 0x000000
                            });
                        } else {
                            viewAdLabel = new PIXI.Text(locale('ui_watch_ad'), {
                                fontFamily: "FredFredBurger",
                                fontSize: 22,
                                align: "left",
                                fill: 0x0
                            });
                        }
                        viewAdLabel.anchor.set(0.5);
                        viewAdLabelHolder.addChild(viewAdLabel);
                        resizeLabelToFitText(viewAdLabel, adBacking.width - viewAdLabelHolder.leftWidth - viewAdLabelHolder.rightWidth);

                        viewAdLabelHolder.width = viewAdLabel.width + viewAdLabelHolder.leftWidth + viewAdLabelHolder.rightWidth;
                        viewAdLabelHolder.position.set(xPositions[j] - (viewAdLabelHolder.width/2), 22);
                        viewAdLabel.position.set(viewAdLabelHolder.width / 2, (viewAdLabelHolder.height/2) + 2);
                    }

                    button = P3.Button.create(
                        texture(data.thumbnail)
                    );
                    button.id = data.id;
                    button.data = data;
                    button.scale.set(0.5); 
                    button.overSoundName = "sfx_btn_generic_rollover";
                    button.signal = button.signals.click.add(this._onItemClick, this);
                    button.position.set(xPositions[j], 171 - (button.height/2));
                    if(data.rotation) {
                        button.rotation = data.rotation * PIXI.DEG_TO_RAD;
                    }
                    this._shopItems.push(button);
                    shelf.addChild(button);

                    if (!isAd) {
                        priceTag = new PIXI.Sprite(texture('price_tag'));
                        priceTag.position.set(xPositions[j], shelf.height - 30);
                        priceTag.anchor.set(0.5, 0);
                        button.priceTag = priceTag;
                        shelf.addChild(priceTag);

                        priceHolder = new PIXI.Container();
                        
                        coinIcon = new PIXI.Sprite(texture('icon_coin'));
                        coinIcon.scale.set(0.75);
                        priceHolder.addChild(coinIcon);

                        if (!iswebfont()) {
                            coinLabel = new PIXI.BitmapText(String(data.price), {
                                fontName: "Alghera Pro",
                                fontSize: 25,
                                align: "left",
                                tint: 0x000000
                            });
                        } else {
                            coinLabel = new PIXI.Text(String(data.price), {
                                fontFamily: "FredFredBurger",
                                fontSize: 25,
                                align: "left",
                                fill: 0x0
                            });
                        }
                        coinLabel.anchor.set(0);
                        coinLabel.position.set(coinIcon.width + 3, !iswebfont() ? 5 : -1);
                        priceHolder.addChild(coinLabel);

                        priceHolder.position.set(-(priceHolder.width/2), 4);
                        priceTag.addChild(priceHolder);
                    } else {
                        
                        priceTag = new PIXI.Sprite(texture('btn_play_add'));
                        priceTag.position.set(xPositions[j], shelf.height - 30);
                        priceTag.scale.set(0.75);
                        priceTag.anchor.set(0.5, 0);
                        button.priceTag = priceTag;
                        shelf.addChild(priceTag);
                    }

                    if (data.quantity > -1 && slot.purchases[data.id] >= data.quantity) {
                        this._sellOut(button);
                    }
                }
            }
        }
    }

    animateShow(){
        this.animateTitleBannerIn();

        this._closeButton.scale.set(0);
        this._closeButton.interactive = false;

        GameScene.active.addTween(gsap.to(this._menuHolder, 0.4, {delay:0.2, y:this._menuHolder.y, ease:Expo.easeOut, onComplete:() => {
            
            this.animateCloseButtonIn();
            this.animateShowFinished();

        }}));
        this._menuHolder.y = common.STAGE_HEIGHT + 100;

        this._inventoryButton.scale.set(0);
        GameScene.active.addTween(gsap.to(this._inventoryButton.scale, 0.3, {x:1, y:1, ease: Sine.easeOut}));

        GameScene.active.addTween(gsap.to(this._winky, 0.5, {y:common.STAGE_HEIGHT+30, ease:Sine.easeIn}));
    }

    animateHide(callback){
        this.animateFillOut();
        this.animateTitleBannerOut();

        this._inventoryButton.interactive = false;

        this._closeButton.interactive = false;
        GameScene.active.addTween(gsap.to(this._closeButton.scale, 0.3, {x:0, y:0, ease:Sine.easeIn}));

        if(this._scrollbar){
            this._menuHolder.removeChild(this._scrollbar);
            this._scrollbar.destroy();
        }

        GameScene.active.addTween(gsap.to(this._menuBacking, 0.3, {height:30, ease:Expo.easeOut, onComplete:() => {
            GameScene.active.addTween(gsap.to(this._menuHolder, 0.4, {y:common.STAGE_HEIGHT + 100, ease:Sine.easeIn, onComplete:() => {
                if (this._boughtCompass) {
                    GameScene.active.hud.compass.reset(GameScene.active.world.getHiddenTreasure());
                }
                callback && callback();
            }}));
        }}));

        GameScene.active.addTween(gsap.to(this._inventoryButton.scale, 0.3, {x:0, y:0, ease: Sine.easeIn}));

        GameScene.active.addTween(gsap.to(this._winky, 0.5, {y:common.STAGE_HEIGHT*2, ease:Sine.easeOut}));
    }

    animateShowFinished()
    {
        if(this._menuContent.height < this._menuMask.height)
            return;

        this._scrollbar = new ScrollBar(texture('scroll_slider'), 245);
        this._scrollbar.y = 50 + (this._scrollbar.height/2) - 10;
        this._scrollbar.x = 340 - (this._scrollbar.width/2) + 14;
        this._menuHolder.addChild(this._scrollbar);
        this._scrollbar.init();

        this._swiper = new Swiper({x:(common.STAGE_WIDTH/2) - (this._menuMask.width/2), y:this._menuMask.worldTransform.ty, width:this._menuMask.width - 60, height:this._menuMask.height});
    }

    _onItemClick(button) {
        this._openPurchaseWindow(button);
    }

    _openPurchaseWindow(button) {
        const data = button.data;

        if (this._swiper) {
            this._swiper.enabled = false;
        }

        const isAd = data.ad && common.ads.isAdEnabled(data.ad);

        this._popUpFill = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._popUpFill.tint = 0x000000;
        this._popUpFill.alpha = 0;
        this._popUpFill.interactive = true;
        this.addChild(this._popUpFill);
        this._resizePopUpFill();

        this.addChild(this._titleBanner);

        const panel = new PIXI.NineSlicePlane(texture("panel_popup_9slice"), 80, 110, 80, 80);
        panel.width = 520;
        panel.height = 310;
        panel.x = (common.STAGE_WIDTH/2) - (panel.width/2);
        panel.y = common.STAGE_HEIGHT;
        this.addChild(panel);

        if (isAd) {
            const viewAdLabelHolder = new PIXI.NineSlicePlane(texture('ad_label'), 17, 7, 17, 7);
            panel.addChild(viewAdLabelHolder);

            let viewAdLabel;
            if (!iswebfont()) {
                viewAdLabel = new PIXI.BitmapText(locale('ui_watch_ad_description'), {
                    fontName: "Alghera Pro",
                    fontSize: 24,
                    align: "left",
                    tint: 0x000000
                });
            } else {
                viewAdLabel = new PIXI.Text(locale('ui_watch_ad_description'), {
                    fontFamily: "FredFredBurger",
                    fontSize: 22,
                    align: "left",
                    fill: 0x0
                });
            }
            viewAdLabel.anchor.set(0.5);
            viewAdLabelHolder.addChild(viewAdLabel);
            resizeLabelToFitText(viewAdLabel, (panel.width - 50) - viewAdLabelHolder.leftWidth - viewAdLabelHolder.rightWidth);

            viewAdLabelHolder.width = viewAdLabel.width + viewAdLabelHolder.leftWidth + viewAdLabelHolder.rightWidth;
            viewAdLabelHolder.position.set((panel.width/2) - (viewAdLabelHolder.width/2), -(viewAdLabelHolder.height) + 10);
            viewAdLabel.position.set(viewAdLabelHolder.width / 2, (viewAdLabelHolder.height/2) + 2);
        }

        let title;
        if (!iswebfont()) {
            title = new PIXI.BitmapText(locale(data.name).toUpperCase(), {
                fontName: "Alghera Pro",
                fontSize: 40,
                align: "center",
                tint: 0x000000
            });
        } else {
            title = new PIXI.Text(locale(data.name).toUpperCase(), {
                fontFamily: "FredFredBurger",
                fontSize: 40,
                align: "center",
                fill: 0x0
            });
        }
        title.anchor.set(0.5);
        title.position.set(panel.width/2, !iswebfont() ? 60 : 53);
        panel.addChild(title);
        resizeLabelToFitText(title, panel.width-60);

        const image = new PIXI.Sprite(texture(data.image));
        image.anchor.set(0.5, 0.5);
        image.position.set(panel.width - 30 - (image.width/2), 60 + (image.height/2));
        panel.addChild(image);
        
        if (data.rotation) {
            image.rotation = data.rotation * PIXI.DEG_TO_RAD;
        }

        let description;
        const descriptionText = isAd && data.adDescription ? data.adDescription : data.description;
        if (!iswebfont()) {
            description = new PIXI.BitmapText(locale(descriptionText), {
                fontName: "Alghera Pro",
                fontSize: 30,
                align: "center",
                tint: 0x702311,
                maxWidth: panel.width-60
            });
        } else {
            description = new PIXI.Text(locale(descriptionText), {
                fontFamily: "FredFredBurger",
                fontSize: 30,
                align: "center",
                fill: 0x702311,
                wordWrap: true,
                wordWrapWidth: panel.width-60
            });
        }
        description.anchor.set(0.5, 0);
        description.position.set(panel.width/2, image.y + (image.height/2) + (!iswebfont() ? 0 : -3));
        panel.addChild(description);

        const bonusHolder = new PIXI.Container();
        bonusHolder.position.set(30, 120);
        panel.addChild(bonusHolder);

        let equippable = false;
        let health = 0;
        const equipment = json("equipment");
        loop: for (let i in data.metadata) {
            for (let j in equipment) {
                for (let k in equipment[j]) {
                    if (equipment[j][k].id == data.metadata[i]) {
                        equippable = true;
                        if (equipment[j][k].stats && equipment[j][k].stats.health != undefined)
                            health += equipment[j][k].stats.health;
                    }
                }
            }
        }

        if(health != 0) {
            const bonusLabel = new PIXI.BitmapText(locale("ui_shop_bonus").toUpperCase(), {
                fontName: "Alghera Pro",
                fontSize: 35,
                align: "left",
                tint: 0x000000
            });
            bonusHolder.addChild(bonusLabel);
            
            const addLabel = new PIXI.BitmapText(health > 0 ? '+' : '-', {
                fontName: "Alghera Pro",
                fontSize: 35,
                align: "left",
                tint: 0x000000
            });
            addLabel.position.set(0, bonusLabel.height);
            bonusHolder.addChild(addLabel);
            let currentX = addLabel.width;
            for (let i = 0; i < Math.ceil(health); i++) {
                let heart = new PIXI.Sprite(texture('icon_life_shield_on_100pc'));
                heart.x = currentX;
                heart.y = addLabel.y;
                bonusHolder.addChild(heart);
                if (i == Math.ceil(health)-1) {
                    let decimal = health - Math.floor(health);
                    if (decimal > 0) {
                        heart.texture = texture("icon_life_shield_off");
                        let newHeart = new PIXI.Sprite(texture("icon_life_shield_on_" + String(decimal*100) + "pc"));
                        newHeart.x = heart.x;
                        newHeart.y = heart.y;
                        bonusHolder.addChild(newHeart);
                    }
                }
                currentX += heart.width;
            }
        }

        const priceHolder = new PIXI.Container();
        priceHolder.position.set(bonusHolder.x, bonusHolder.y + bonusHolder.height + 20);
        panel.addChild(priceHolder);

        const costLabel = new PIXI.BitmapText(locale("ui_shop_cost").toUpperCase(), {
            fontName: "Alghera Pro",
            fontSize: 35,
            align: "left",
            tint: 0x000000
        });
        priceHolder.addChild(costLabel);
                    
        const coinIcon = new PIXI.Sprite(texture('icon_coin'));
        coinIcon.y = costLabel.height + 3;
        priceHolder.addChild(coinIcon);
        
        const coinLabel = new PIXI.BitmapText(String(data.price), {
            fontName: "Alghera Pro",
            fontSize: 35,
            align: "left",
            tint: 0x000000
        });
        coinLabel.anchor.set(0);
        coinLabel.position.set(coinIcon.width + 3, coinIcon.y + 5);
        priceHolder.addChild(coinLabel);

        if(isAd) {
            priceHolder.visible = false;
            image.x = panel.width/2;
        }

        panel.height = Math.max(panel.height, description.y + description.height + 80);

        let errorMessage;

        const hidePanel = (callback) => {
            this._tweens.push(
                gsap.to(panel, 0.3, {y:common.STAGE_HEIGHT, ease:Expo.easeIn, onComplete:() => {
                    this.removeChild(panel);
                    if (callback) {
                        callback();
                    }
                }}),
                gsap.to(this._popUpFill, 0.2, {alpha:0, ease:Sine.easeIn, onComplete:() => {
                    this.removeChild(this._popUpFill);
                    this._popUpFill = null;
                }})
            );
            if (errorMessage) {
                try{
                    this.removeChild(errorMessage);
                } catch(e){}
                errorMessage.destroy();
                errorMessage = null;
            }
            if (this._swiper) {
                this._swiper.enabled = true;
            }
        }

        const buttonHolder = new PIXI.Container();
        buttonHolder.y = panel.height;
        panel.addChild(buttonHolder);

        let backing, nameLabel;
        const buyButton = P3.Button.create(PIXI.Texture.EMPTY);
            backing = new PIXI.NineSlicePlane(texture("btn_rectangle_out"), 46, 20, 46, 20);
            backing.y = -backing.height/2;
            buyButton._background.addChild(backing);
        buyButton.overSoundName = "sfx_btn_generic_rollover";
        buyButton.clickSoundName = "sfx_btn_generic_press_00";

        if (!iswebfont()) {
            nameLabel = new PIXI.BitmapText(locale(isAd ? "ui_watch_ad" : "ui_shop_buy").toUpperCase(), {
                fontName: "Alghera Pro Outline",
                fontSize: 32,
                align: "left",
                tint: 0xFFFFFF
            });    
        } else {
            nameLabel = new PIXI.Text(locale(isAd ? "ui_watch_ad" : "ui_shop_buy").toUpperCase(), {
                fontName: "Alghera Pro Outline",
                fontSize: 32,
                align: "left",
                tint: 0xFFFFFF
            });    
        }

        nameLabel.anchor.set(0.5);
        buyButton._background.addChild(nameLabel);
        resizeLabelToFitText(nameLabel, isAd ? 300 : 180);
        backing.width = nameLabel.width + backing.leftWidth + backing.rightWidth;
        backing.x = -backing.width/2;

        buyButton.x = (buyButton.width/2);
        buttonHolder.addChild(buyButton);

        const closeButton = P3.Button.create(
            texture("btn_small_out"),
            texture("btn_small_out"),
            texture("btn_small_out"),
            texture("btn_icon_cross"),
        );
        closeButton.x = panel.width + 20;
        closeButton.y = 20;
        panel.addChild(closeButton);
        
        this.signalListeners.push(
            buyButton.signals.click.add(() => {

                common.ads.signals.viewed.detachAll();
                common.ads.signals.fail.detachAll();
                if (isAd) {
                    if (common.ads.showAd()) {
                        buyButton.interactive = closeButton.interactive = false;
                        buyButton.alpha = closeButton.alpha = 0.5;

                        this.signalListeners.push(
                            common.ads.signals.viewed.once((rewarded) => {
                                if (rewarded){
                                    this._purchase(data, button, false, false);
                                    hidePanel();
                                } else {
                                    buyButton.interactive = closeButton.interactive = true;
                                    buyButton.alpha = closeButton.alpha = 1;
                                }
                            }),
                            common.ads.signals.fail.once(() => {
                                buyButton.interactive = closeButton.interactive = true;
                                buyButton.alpha = closeButton.alpha = 1;

                                if(errorMessage == null) {
                                    errorMessage = new IdleMessage(locale("ad_fail_message"), () => {
                                        try{
                                            this.removeChild(errorMessage);
                                        } catch(e){}
                                        errorMessage.destroy();
                                        errorMessage = null;
                                    });
                                    errorMessage.x = (common.STAGE_WIDTH / 2) - (P3.View.width / 2);
                                    errorMessage.y = 150;
                                    this.addChild(errorMessage);
                                }
                            })
                        );
                    } else {
                        this._purchase(data, button, false, false);
                        hidePanel();
                    }
                } else {
                    this._purchase(data, button, false);
                    hidePanel();
                }
            }, this),
            closeButton.signals.click.once(() => {
                hidePanel();
            }, this)
        );
        
        let equipButton;
        if (equippable) {
            equipButton = P3.Button.create(PIXI.Texture.EMPTY);
                backing = new PIXI.NineSlicePlane(texture("btn_rectangle_out"), 46, 20, 46, 20);
                backing.y = -backing.height/2;
                equipButton._background.addChild(backing);
            equipButton.overSoundName = "sfx_btn_generic_rollover";
            equipButton.clickSoundName = "sfx_btn_generic_press_00";

            if (!iswebfont()) {
                nameLabel = new PIXI.BitmapText(locale("ui_shop_buy_and_equip").toUpperCase(), {
                    fontName: "Alghera Pro Outline",
                    fontSize: 32,
                    align: "left",
                    tint: 0xFFFFFF
                });    
            } else {
                nameLabel = new PIXI.Text(locale("ui_shop_buy_and_equip").toUpperCase(), {
                    fontName: "Alghera Pro Outline",
                    fontSize: 32,
                    align: "left",
                    tint: 0xFFFFFF
                });    
            }

            nameLabel.anchor.set(0.5);
            equipButton._background.addChild(nameLabel);
            resizeLabelToFitText(nameLabel, 350);
            backing.width = nameLabel.width + (backing.leftWidth / (nameLabel.width > 180 ? 2 : 1)) + (backing.rightWidth / (nameLabel.width > 180 ? 2 : 1));
            backing.x = -backing.width/2;

            equipButton.x = buyButton.width + 10 + (equipButton.width/2)
            buttonHolder.addChild(equipButton);

            this.signalListeners.push(
                equipButton.signals.click.add(() => {
                    hidePanel();
                    this._purchase(data, button, true);
                }, this)
            );
        }
        buttonHolder.x = (panel.width/2) - (buttonHolder.width/2);

        if (data.price > common.saveData.activeSlot.coins && !isAd) {
            const colorMatrix = new PIXI.filters.ColorMatrixFilter();
            colorMatrix.desaturate();

            this._coinHolder.promptMoneyBag();

            buyButton.filters = [colorMatrix];
            if (equippable) {
                equipButton.filters = [colorMatrix];
                equipButton.interactive = false
            }
            buyButton.interactive = false;
        }

        this._tweens.push(
            gsap.to(panel, 0.3, {y:(common.STAGE_HEIGHT / 2) - (panel.height/2) + 70, ease:Expo.easeOut}),
            gsap.to(this._popUpFill, 0.2, {alpha:0.3, ease:Sine.easeIn})
        );
    }

    _purchase(data, button, equip, spendCoins = true) {
        this._closeButton.interactive = false;
        this._inventoryButton.interactive = false;

        const slot = common.saveData.activeSlot;
        const oldCoins = slot.coins;

        if (slot.purchases == null) {
            slot.purchases = {};
        }
        if (slot.purchases[data.id] == null) {
            slot.purchases[data.id] = 0;
        }
        slot.purchases[data.id]++;

        if (spendCoins) {
            slot.coins -= data.price;
            GameScene.active.hud.coinHolder.coinAmount = slot.coins;
        }

        P3.audio.playSound("sfx_btn_shop_purchase");
        
        if (data.quantity > -1 && slot.purchases[data.id] >= data.quantity) {
            this._sellOut(button);
        }

        common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.INTERACTION, {
            section: common.tracking.getSection(),
            contenttitle: json('locale')[data.name].en,
            interaction: "shop buy" + (equip ? " and equip": ""),
            progress: common.tracking.progress,
            interactionevent: 1
        });

        let animateIcon = true;

        if(data.metadata.item == "coins") {
            animateIcon = false;

            slot.coins += data.metadata.value;
            GameScene.active.hud.coinHolder.coinAmount = slot.coins;

            const pos = button.toGlobal(new PIXI.Point(0, 0));
            pos.x += (common.STAGE_WIDTH/2) - (P3.View.width/2);
            const coinEffect = new CoinAnimation(data.metadata.value, GameScene.active, (percent) => {
                this._coinHolder.coinAmount = oldCoins + Math.round(percent * data.metadata.value);
            }, {x:-(pos.x - (this._titleBanner.x + this._coinHolder.x)), y:-(pos.y - (this._titleBanner.y + this._coinHolder.y))});
            coinEffect.x = pos.x;
            coinEffect.y = pos.y;
            this.addChild(coinEffect);
        }
        if (animateIcon) {
            const tweenIcon = new PIXI.Sprite(button._background.texture);
            tweenIcon.anchor.set(0.5);
            tweenIcon.scale.set(button.scale.x);
            const pos = button.toGlobal(new PIXI.Point(0, 0));
            tweenIcon.position.set((common.STAGE_WIDTH/2) - (P3.View.width/2) + pos.x, pos.y);
            this.addChild(tweenIcon);

            this._purchasingItems++;

            const v = new P3.Vector2(pos.x - this._inventoryButton.x, pos.y - this._inventoryButton.y);
            const seq = new gsap.timeline({onComplete:() => {
                this._purchasingItems--;
                if (this._purchasingItems <= 0) {
                    this._closeButton.interactive = true;
                    this._inventoryButton.interactive = true;
                }
            }});
            GameScene.active.addTween(seq);
            
            seq.to(tweenIcon, (v.length / 100) * 0.15, {delay:0.2, x:this._inventoryButton.x, y:this._inventoryButton.y, ease:Sine.easeInOut, 
                onStart:() => {
                    
                },
                onComplete:() => {
                    this.removeChild(tweenIcon);
                    if (spendCoins) {
                        const coinEffect = new CoinAnimation(data.price, GameScene.active, (percent) => {
                            this._coinHolder.coinAmount = oldCoins - Math.round(percent * data.price);
                        }, {x:-350, y:200});
                        coinEffect.x = this._titleBanner.x + this._coinHolder.x;
                        coinEffect.y = this._titleBanner.y + this._coinHolder.y;
                        this.addChild(coinEffect);
                    }
            }})
            
            seq.to(this._inventoryButton.scale, 0.3, {x:1.1, y:1.1, ease:Sine.easeOut});
            seq.to(this._inventoryButton.scale, 0.7, {x:1, y:1, ease:Elastic.easeOut, onComplete: () => {
                delay(0.1, () => {
                    
                }, this);
            }});
        } else {
            this._closeButton.interactive = true;
            this._inventoryButton.interactive = true;
        }

        if (equip) {
            P3.audio.playSound("sfx_btn_item_equip_00");
            
            const equipment = json('equipment');

            let totalHealth = 0;

            for (let i in definitions.EQUIPPABLES) {
                if (data.metadata[definitions.EQUIPPABLES[i]]) {
                    for (let j in equipment){
                        for (let k in equipment[j]) {
                            if(equipment[j][k].id == common.saveData.activeSlot.player[definitions.EQUIPPABLES[i]]) {
                                if (equipment[j][k].stats.effect) {
                                    delete common.saveData.activeSlot.player.effects[equipment[j][k].stats.effect];
                                }
                            }
                        }
                    }
                    for (let j in equipment) {
                        for (let k in equipment[j]) {
                            if (equipment[j][k].id == data.metadata[definitions.EQUIPPABLES[i]] && equipment[j][k].stats) {
                                if (equipment[j][k].stats.health) {
                                    totalHealth += equipment[j][k].stats.health;
                                }
                                if (equipment[j][k].stats.range) {
                                    GameScene.active.player.setAttackRange(equipment[j][k].stats.range);
                                }
                                if (equipment[j][k].stats.power) {
                                    GameScene.active.player.setAttackPower(equipment[j][k].stats.power);
                                }
                                if (equipment[j][k].stats.effect) {
                                    common.saveData.activeSlot.player.effects[equipment[j][k].stats.effect] = true;
                                }
                            }
                            if (equipment[j][k].id == common.saveData.activeSlot.player[definitions.EQUIPPABLES[i]] && equipment[j][k].stats) {
                                if (equipment[j][k].stats.health) {
                                    totalHealth -= equipment[j][k].stats.health;
                                }
                            }
                        }
                    }
                    common.saveData.activeSlot.player[definitions.EQUIPPABLES[i]] = data.metadata[definitions.EQUIPPABLES[i]];
                    GameScene.active.player.applySkins();
                    if (totalHealth != 0) {
                        const oldHealth = GameScene.active.hud.healthBar.health;
                        const hadFullHealth = oldHealth == GameScene.active.hud.healthBar.healthMax;
                        const newHealth = hadFullHealth ? null : Math.min(oldHealth, (common.saveData.activeSlot.player.hearts + totalHealth) * 4);
                        GameScene.active.setPlayerHearts(common.saveData.activeSlot.player.hearts + totalHealth, true, newHealth);
                        this._addHealthBar();
                        this._healthBar.setHearts(common.saveData.activeSlot.player.hearts, true);
                        if (newHealth != null) {
                            this._healthBar.health = newHealth;
                        }
                    }
                }
            }
        }

        if (data.metadata.item) {
            switch (data.metadata.item) {
                case "add_heart_container":
                    GameScene.active.setPlayerHearts(common.saveData.activeSlot.player.hearts + 1, true);
                    break;
                case "replenish_1_health":
                    GameScene.active.playerRestoreHealth(4);
                    break;
                case "replenish_all_health":
                    GameScene.active.playerRestoreHealth(4 * common.saveData.activeSlot.player.hearts);
                    break;
                case "enable_compass":
                    GameScene.active.hud.addCompass();
                    GameScene.active.world.addHiddenTreasureSpots();
                    this._boughtCompass = true;
                    GameScene.active.assignQuestManually('hidden_treasure', 'badger');
                    break;
                default:
                    break;
            }
            if (data.metadata.item == "add_heart_container" ||
                data.metadata.item == "replenish_1_health" ||
                data.metadata.item == "replenish_all_health") {
                this._addHealthBar();
                this._healthBar.setHearts(common.saveData.activeSlot.player.hearts, true);
                this._healthBar.health = GameScene.active.hud.healthBar.health;
            }
        }
        if (data.metadata.effect) {
            if (common.saveData.activeSlot.player.effects == null) {
                common.saveData.activeSlot.player.effects = {};
            }
            if (common.saveData.activeSlot.player.effects[data.metadata.effect] == null) {
                common.saveData.activeSlot.player.effects[data.metadata.effect] = 0;
            }
            common.saveData.activeSlot.player.effects[data.metadata.effect] += data.metadata.value;
            GameScene.active.setPlayerHearts(common.saveData.activeSlot.player.hearts, false, GameScene.active.hud.healthBar.health);
            this._addHealthBar();
            this._healthBar.setHearts(common.saveData.activeSlot.player.hearts);
            this._healthBar.health = GameScene.active.hud.healthBar.health;
        }


        common.saveData.saveGame();
    }

    _sellOut(button) {
        button.visible = button.interactive = false;
        button.priceTag.visible = false;

        let soldOut;
        if (!iswebfont()) {
            soldOut = new PIXI.BitmapText(locale('ui_shop_sold_out').toUpperCase(), {
                fontName: "Alghera Pro",
                fontSize: 25,
                align: "center",
                tint: 0x000000,
                maxWidth: 150
            });
        } else {
            soldOut = new PIXI.Text(locale('ui_shop_sold_out').toUpperCase(), {
                fontName: "Alghera Pro",
                fontSize: 25,
                align: "center",
                tint: 0x000000,
                maxWidth: 150
            });
        }

        soldOut.anchor.set(0.5);
        soldOut.position.set(button.x, 120);
        button.parent.addChild(soldOut);
    }

    _resizePopUpFill() {
        if (this._popUpFill) {
            this._popUpFill.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
            this._popUpFill.width = P3.View.width;
            this._popUpFill.height = P3.View.height;
        }
    }

    _addHealthBar() {
        if (this._healthBar == null) {
            this._healthBar = new HealthBar();
            this._healthBar.position.set(common.STAGE_WIDTH * 0.5, 0);
            this.addChild(this._healthBar);
        }
    }
}

/**
 * @static
 * @type {ShopScene}
 */
ShopScene.active = null;